/** Orthogonal wire routing helpers. All points are in sheet millimetres. */
import type { Point } from '@elec/domain';

export type Dir = 'up' | 'down' | 'left' | 'right';

export const GRID = 2.5;
export const snap = (v: number, g = GRID) => Math.round(v / g) * g;
export const snapPoint = (p: Point, g = GRID): Point => ({ x: snap(p.x, g), y: snap(p.y, g) });

const vertical = (d: Dir | null | undefined) => d === 'up' || d === 'down';
const eq = (a: number, b: number) => Math.abs(a - b) < 1e-6;

/**
 * Corner points between a and b so that every segment is horizontal or vertical.
 * `aDir` is the direction the wire leaves a (port direction, or the previous segment's
 * direction); `bDir` the direction of the port at b (the wire arrives against it).
 */
export function route(a: Point, aDir: Dir | null, b: Point, bDir: Dir | null): Point[] {
  if (eq(a.x, b.x) || eq(a.y, b.y)) return [];
  const aV = aDir ? vertical(aDir) : Math.abs(b.y - a.y) >= Math.abs(b.x - a.x);
  const bV = bDir ? vertical(bDir) : null;
  if (aV) {
    if (bV === true) {
      const midY = snap((a.y + b.y) / 2);
      return [
        { x: a.x, y: midY },
        { x: b.x, y: midY },
      ];
    }
    return [{ x: a.x, y: b.y }];
  }
  if (bV === false) {
    const midX = snap((a.x + b.x) / 2);
    return [
      { x: midX, y: a.y },
      { x: midX, y: b.y },
    ];
  }
  return [{ x: b.x, y: a.y }];
}

/** Direction of the segment from a to b (for an orthogonal segment). */
export function segmentDir(a: Point, b: Point): Dir | null {
  if (eq(a.x, b.x) && eq(a.y, b.y)) return null;
  if (eq(a.x, b.x)) return b.y > a.y ? 'down' : 'up';
  if (eq(a.y, b.y)) return b.x > a.x ? 'right' : 'left';
  return null;
}

/** Remove duplicate and collinear interior points from a polyline. */
export function simplify(points: Point[]): Point[] {
  const out: Point[] = [];
  for (const p of points) {
    const last = out.at(-1);
    if (last && eq(last.x, p.x) && eq(last.y, p.y)) continue;
    out.push(p);
    while (out.length >= 3) {
      const [a, b, c] = out.slice(-3) as [Point, Point, Point];
      const collinear = (eq(a.x, b.x) && eq(b.x, c.x)) || (eq(a.y, b.y) && eq(b.y, c.y));
      if (!collinear) break;
      out.splice(out.length - 2, 1);
    }
  }
  return out;
}

/** Closest point to p on segment ab, with its distance. */
export function projectOnSegment(p: Point, a: Point, b: Point): { point: Point; dist: number; t: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  const point = { x: a.x + t * dx, y: a.y + t * dy };
  return { point, dist: Math.hypot(p.x - point.x, p.y - point.y), t };
}
